import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import dayjs, { type Dayjs } from 'dayjs'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { DayjsFormats } from 'shared/lib/dayjs'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { getNextQuietHoursSendAt } from 'entities/QuietHours'
import { QuietHours } from 'entities/QuietHours/model'
import { ScheduleContent } from 'widgets/ConversationSchedule/ui/ScheduleContent/ScheduleContent'

export function getScheduleText({
  tz,
  send_at,
  scheduleFor = false,
}: {
  tz: string
  send_at: Dayjs
  scheduleFor?: boolean
}): string {
  const effectiveTz = tz || dayjs.tz.guess()
  const todayInTZ = dayjs().tz(effectiveTz).format(DayjsFormats.dateSort)
  const sendAtDate = send_at.tz(effectiveTz).format(DayjsFormats.dateSort)
  const tomorrowInTZ = dayjs().tz(effectiveTz).add(1, 'day').format(DayjsFormats.dateSort)
  const pre = scheduleFor ? 'Schedule for ' : 'Schedule '
  const time = send_at.format(DayjsFormats.time2)

  if (sendAtDate === todayInTZ) return `${pre}today at ${time}`
  if (sendAtDate === tomorrowInTZ) return `${pre}tomorrow at ${time}`
  const full = send_at.tz(effectiveTz).format(DayjsFormats.fullWithAt)
  return scheduleFor ? `Schedule for ${full}` : `Schedule on ${full}`
}

export type IScheduledData = {
  send_at?: string
  is_contact_timezone?: boolean
  stop_on_response?: boolean
}

type IOnScheduleProps = ({ scheduledData }: { scheduledData: IScheduledData }) => void

export class ConversationScheduleStore {
  private _modalId = ''
  private _contactTZ = ''
  private _scheduleDate: Dayjs = dayjs(0)
  private _is_contact_timezone = true
  private _stop_on_response = false
  private _onSchedule: IOnScheduleProps | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get scheduleDate() {
    return this._scheduleDate
  }

  get stop_on_response() {
    return this._stop_on_response
  }

  get is_contact_timezone() {
    return this._is_contact_timezone
  }

  get contactDateString() {
    return this._scheduleDate
      .tz(uiStore.timezone, true)
      .tz(this._contactTZ)
      .format(DayjsFormats.fullWithAt)
  }

  get offsetMinutes() {
    return (
      dayjs
        .tz(dayjs(), this._is_contact_timezone ? this._contactTZ : uiStore.timezone)
        .utcOffset() - dayjs().utcOffset()
    )
  }

  get isAlreadyPassed() {
    const sendDate = this._is_contact_timezone
      ? dayjs(this._scheduleDate).tz(this._contactTZ, true).utc().valueOf()
      : dayjs(this._scheduleDate).utc().valueOf()
    const nowDate = dayjs().utc().valueOf()

    return sendDate < nowDate
  }

  get scheduleText() {
    const tz = this._is_contact_timezone ? this._contactTZ : dayjs.tz.guess()
    const sendAtForDisplay =
      this._is_contact_timezone && this._contactTZ
        ? dayjs(this._scheduleDate).tz(this._contactTZ, true)
        : this._scheduleDate
    return getScheduleText({
      tz: tz || dayjs.tz.guess(),
      send_at: sendAtForDisplay,
    })
  }

  get quietHours() {
    return new QuietHours(this._entities.organizationStore.quietHoursSettings)
  }

  get isQuietHoursEnabled() {
    return this.quietHours.isQuietHoursEnabled
  }

  get isQuietHoursBlockOnWeekend() {
    return this.quietHours.isQuietHoursBlockOnWeekend
  }

  get quietHoursAllowedTimeFrom() {
    return this.quietHours.formatedQuietHours.allowedTimeFrom
  }

  get quietHoursAllowedTimeTo() {
    return this.quietHours.formatedQuietHours.allowedTimeTo
  }

  get isScheduleMessageInsideQuietHours() {
    if (!this.isQuietHoursEnabled) return false
    if (!this._scheduleDate.isValid()) return false

    const tz = this._is_contact_timezone ? this._contactTZ : uiStore.timezone
    const effectiveTz = tz || dayjs.tz.guess()
    const scheduleInTz = this._is_contact_timezone
      ? dayjs(this._scheduleDate).tz(effectiveTz, true)
      : dayjs(this._scheduleDate).tz(effectiveTz)
    const scheduleMinutes = scheduleInTz.hour() * 60 + scheduleInTz.minute()
    const scheduleDay = scheduleInTz.day()

    if (this.quietHours.isQuietHoursBlockOnWeekend && (scheduleDay === 0 || scheduleDay === 6)) {
      return true
    }

    const from = this.quietHours.quietHoursAllowedTimeFrom
    const to = this.quietHours.quietHoursAllowedTimeTo
    if (!from || !to) return false

    const convertToMinutes = (s: string): number | null => {
      const parts = s.split(':')
      const h = parseInt(parts[0], 10)
      const m = parseInt(parts[1] ?? '0', 10)
      return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m
    }
    const fromMinutes = convertToMinutes(from)
    const toMinutes = convertToMinutes(to)
    if (fromMinutes === null || toMinutes === null) return false

    if (fromMinutes <= toMinutes) {
      return scheduleMinutes < fromMinutes || scheduleMinutes > toMinutes
    }
    return scheduleMinutes > toMinutes && scheduleMinutes < fromMinutes
  }

  reset = () => {
    this._contactTZ = ''
    this._scheduleDate = dayjs(0)
    this._is_contact_timezone = true
    this._stop_on_response = false
  }

  setOpenModal = ({
    contactTZ,
    send_at,
    is_contact_timezone,
    stop_on_response,
    onSchedule,
  }: {
    contactTZ: string
    onSchedule: IOnScheduleProps
    send_at?: Dayjs | Date | null
  } & Omit<IScheduledData, 'send_at'>) => {
    const originalWasDate = send_at instanceof Date
    if (originalWasDate && send_at) {
      if (is_contact_timezone) {
        const tzDate = dayjs(send_at).utc(true).tz(contactTZ)
        send_at = tzDate.utcOffset(0, true).tz(dayjs.tz.guess(), true)
      } else {
        send_at = dayjs(send_at).utc(true).tz(dayjs.tz.guess())
      }
    }

    let sendAtDayjs: Dayjs | null = null
    if (send_at) {
      sendAtDayjs = dayjs.isDayjs(send_at) ? send_at : dayjs(send_at)
    }
    const useContactTz = (is_contact_timezone ?? true) && !!contactTZ
    const browserTz = dayjs.tz.guess()

    let scheduleDate: Dayjs
    if (sendAtDayjs !== null) {
      scheduleDate = sendAtDayjs
      if (!originalWasDate && useContactTz) {
        const inContactTz = sendAtDayjs.tz(contactTZ)
        scheduleDate = dayjs()
          .tz(browserTz)
          .year(inContactTz.year())
          .month(inContactTz.month())
          .date(inContactTz.date())
          .hour(inContactTz.hour())
          .minute(inContactTz.minute())
          .second(0)
          .millisecond(0)
      }
    } else {
      const quietHours = this.quietHours
      const nextQh =
        quietHours.isQuietHoursEnabled &&
        getNextQuietHoursSendAt({
          contactTZ,
          allowed_time_from: quietHours.quietHoursAllowedTimeFrom,
          allowed_time_to: quietHours.quietHoursAllowedTimeTo,
          is_block_on_weekend: quietHours.isQuietHoursBlockOnWeekend,
        })
      if (nextQh && useContactTz) {
        const inContactTz = nextQh
        scheduleDate = dayjs()
          .tz(browserTz)
          .year(inContactTz.year())
          .month(inContactTz.month())
          .date(inContactTz.date())
          .hour(inContactTz.hour())
          .minute(inContactTz.minute())
          .second(0)
          .millisecond(0)
      } else {
        scheduleDate = dayjs().add(1, 'hour').startOf('hour')
      }
    }

    this._modalId = nanoid()
    this._contactTZ = contactTZ
    this._scheduleDate = scheduleDate
    this._is_contact_timezone = is_contact_timezone ?? true
    this._stop_on_response = stop_on_response ?? false
    this._onSchedule = onSchedule

    modalStore.addModal({
      id: this._modalId,
      pureContent: true,
      ModalContentProps: {
        store: this,
      },
      ModalContent: ScheduleContent,
      title: 'Schedule',
      width: 344,
    })
  }

  onCloseModal = () => {
    modalStore.closeModal(this._modalId)
    this.reset()
  }

  checkAlreadyPassed() {
    return this.isAlreadyPassed
  }

  handleSchedule = () => {
    const isAlreadyPassed = this.checkAlreadyPassed()

    if (!isAlreadyPassed && this._onSchedule) {
      const sendDate = this._is_contact_timezone
        ? dayjs(this._scheduleDate).tz(this._contactTZ, true).utc()
        : dayjs(this._scheduleDate).utc()

      this._onSchedule({
        scheduledData: {
          is_contact_timezone: this._is_contact_timezone,
          stop_on_response: this._stop_on_response,
          send_at: sendDate.format(DayjsFormats.apiFormat),
        },
      })
      this.onCloseModal()
    }
  }

  toggleIsUseContactTimezone = () => {
    this._is_contact_timezone = !this._is_contact_timezone
    this.checkAlreadyPassed()
  }
  toggleIsCancelOnResponse = () => {
    this._stop_on_response = !this._stop_on_response
  }

  setScheduleDate = (date: Date | null) => {
    this._scheduleDate = dayjs(date || 0)
    this.checkAlreadyPassed()
  }
}
